import { Box, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { monetaryFormat } from '@/utils/monetary-format';
import type { MappedFeature } from '../utils/map-graphql-to-calculator.util';

interface BooleanAddOnRowProps {
    feature: MappedFeature;
    enabled: boolean;
    currency?: { symbol: string } | null;
    onChange: (code: string, enabled: boolean) => void;
    disabled?: boolean;
}

export const BooleanAddOnRow = ({ feature, enabled, currency, onChange, disabled }: BooleanAddOnRowProps) => {
    const { t } = useTranslation();
    const included = feature.value === 1;
    const basePrice = feature.addOnPrice ?? 0;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'auto 1fr', md: '1fr 80px 140px' },
                gridTemplateRows: { xs: 'auto auto', md: '1fr' },
                alignItems: 'center',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                gap: { xs: 1, md: 2 },
            }}
        >
            {/* Label */}
            <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Typography variant="body2" fontWeight={600}>
                    {t(feature.label)}
                </Typography>
                {feature.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t(feature.description)}
                    </Typography>
                )}
                <Typography variant="caption" color={included ? 'success.main' : 'text.secondary'}>
                    {included
                        ? t('billing.calculator.addon.included-in-plan')
                        : t('billing.calculator.addon.not-included-in-plan')}
                </Typography>
            </Box>

            {/* Toggle — disabled and on when already included, or when parent dependency is not met */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Switch
                    size="small"
                    checked={included || enabled}
                    disabled={included || !!disabled}
                    onChange={(_, checked) => onChange(feature.code, checked)}
                    color="primary"
                />
            </Box>

            {/* Price */}
            <Box sx={{ textAlign: 'right', alignSelf: 'center' }}>
                {included ? (
                    <Typography variant="body2" color="text.disabled">
                        {t('billing.calculator.addon.included')}
                    </Typography>
                ) : enabled ? (
                    <>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {t('billing.calculator.addon.upgrade-cost')}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                            +{monetaryFormat(basePrice, currency)}
                            {t('billing.calculator.per-month')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2" color="text.disabled">
                        +{monetaryFormat(basePrice, currency)}
                        {t('billing.calculator.per-month')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
